import { useEffect, useState } from 'react';
import { isValidPinFormat, isWeakPin } from '@abastece/shared';
import type { DeviceKind } from '@abastece/core';
import { setMeta } from '../db/dexie.js';
import { ApiError, OfflineError, api } from '../sync/client.js';
import { sincronizar } from '../sync/engine.js';
import { useT, type Clave } from '../i18n/index.js';
import { Idioma } from '../ui/Idioma.js';
import { Ico } from '../ui/Iconos.js';

/**
 * First run. Creates the owner and registers this device as the first terminal — or, on a shop
 * that is already set up, registers this device on the owner's PIN (`RegistrarEquipo`).
 *
 * Needs connectivity, and that is not a limitation to work around: device codes are assigned
 * centrally so two terminals can never issue the same ticket number, which is precisely what
 * makes every later operation safe to perform offline.
 */
export function Instalar({ onListo }: { onListo: () => void }) {
  const t = useT();
  const [setupToken, setSetupToken] = useState('');
  const [nombre, setNombre] = useState('');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [dispositivoNombre, setDispositivoNombre] = useState('Mostrador');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  /**
   * A development server with no SETUP_TOKEN configured says so, and then this asks for the two
   * things that actually make an account: a name and a PIN. Undefined until the server answers,
   * so the extra fields never flash on screen and then disappear.
   */
  const [abierta, setAbierta] = useState<boolean | undefined>(undefined);
  /** Somebody already set this shop up: this device joins it rather than installing another. */
  const [instalado, setInstalado] = useState(false);
  useEffect(() => {
    let vivo = true;
    void api
      .health()
      .then((h) => {
        if (!vivo) return;
        setAbierta(Boolean(h.instalacionAbierta));
        setInstalado(Boolean(h.instalado));
      })
      .catch(() => vivo && setAbierta(false));
    return () => {
      vivo = false;
    };
  }, []);

  const problemaPin = !pin
    ? null
    : !isValidPinFormat(pin)
      ? t('instalar.pinCorto')
      : isWeakPin(pin)
        ? t('instalar.pinDebil')
        : !abierta && pin2 && pin !== pin2
          ? t('instalar.pinNoCoincide')
          : null;

  const listo =
    nombre.trim() && pin && !problemaPin && (abierta ? true : setupToken.trim() && pin === pin2);

  if (abierta === false && instalado) return <RegistrarEquipo onListo={onListo} />;

  async function instalar() {
    setOcupado(true);
    setError(null);
    try {
      const res = await api.bootstrap({
        setupToken: abierta ? 'dev-setup-token' : setupToken,
        nombre: nombre.trim(),
        pin,
        dispositivoNombre: dispositivoNombre.trim() || 'Mostrador',
      });
      await setMeta('dispositivoId', res.dispositivoId);
      await setMeta('dispositivoCodigo', res.codigo);
      await setMeta('token', res.token);
      await setMeta('usuarioActivoId', res.usuarioId);
      // Bootstrap already consumed device sequences 1 and 2 on the server.
      await setMeta('proximoDeviceSeq', 3);
      await setMeta('ultimoServerSeq', 0);
      await sincronizar();
      onListo();
    } catch (e) {
      setError(
        e instanceof OfflineError
          ? t('instalar.sinInternet')
          : e instanceof ApiError
            ? e.message
            : t('instalar.fallo'),
      );
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Abastece Ya</h1>
          <p className="mt-1 text-sm text-slate-400">
            {t(abierta ? 'instalar.tituloAbierta' : 'instalar.tituloCerrada')}
          </p>
        </div>
        <Idioma tamano="compacto" />
      </header>

      <div className="tarjeta flex flex-col gap-4">
        {abierta === false && (
          <div>
            <label className="etiqueta" htmlFor="setup">
              {t('instalar.clave')}
            </label>
            <input
              id="setup"
              className="campo"
              value={setupToken}
              onChange={(e) => setSetupToken(e.target.value)}
              autoComplete="off"
              placeholder={t('instalar.clavePlaceholder')}
            />
          </div>
        )}

        <div>
          <label className="etiqueta" htmlFor="nombre">
            {t('instalar.nombreDueno')}
          </label>
          <input
            maxLength={80}
            id="nombre"
            className="campo"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            autoComplete="off"
          />
        </div>

        <div>
          <label className="etiqueta" htmlFor="pin">
            {t('instalar.pin')}
          </label>
          <input
            id="pin"
            className="campo tabular"
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
        </div>

        {abierta === false && (
          <div>
            <label className="etiqueta" htmlFor="pin2">
              {t('instalar.repitaPin')}
            </label>
            <input
              id="pin2"
              className="campo tabular"
              type="password"
              inputMode="numeric"
              value={pin2}
              onChange={(e) => setPin2(e.target.value.replace(/\D/g, ''))}
            />
          </div>
        )}

        {problemaPin && <p className="text-sm text-rose-400">{problemaPin}</p>}

        {abierta === false && (
          <div>
            <label className="etiqueta" htmlFor="disp">
              {t('instalar.nombreDispositivo')}
            </label>
            <input
              maxLength={60}
              id="disp"
              className="campo"
              value={dispositivoNombre}
              onChange={(e) => setDispositivoNombre(e.target.value)}
            />
          </div>
        )}
      </div>

      {abierta && (
        <p className="-mt-2 text-xs text-slate-600">{t('instalar.avisoDesarrollo')}</p>
      )}

      {error && <p className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</p>}

      <button
        type="button"
        className="btn-primario w-full"
        disabled={!listo || ocupado}
        onClick={() => void instalar()}
      >
        <Ico icono="negocio" />
        {t(
          ocupado
            ? abierta
              ? 'instalar.creando'
              : 'instalar.instalando'
            : abierta
              ? 'instalar.botonAbierta'
              : 'instalar.botonCerrada',
        )}
      </button>
    </div>
  );
}

/**
 * What a new device can be. «Camión», the truck's, went with «Reparto» (2026-10-04): whoever
 * delivers does it on a phone of their own. Devices registered as one keep the name.
 */
const TIPOS: readonly DeviceKind[] = ['telefono', 'mostrador', 'admin'];

/**
 * A device joining a shop that is already set up: the cashier's phone, the driver's, a second
 * till. The owner picks themselves and types their PIN on it — the one thing only they can do —
 * and the server gives it its code. Everything else, the people who sign in on it included,
 * comes down with the first sync.
 *
 * Until this existed a shop could only ever have the device it was installed on: the install
 * screen offered to install again, and the server rightly refused a shop that exists.
 */
function RegistrarEquipo({ onListo }: { onListo: () => void }) {
  const t = useT();
  const [duenos, setDuenos] = useState<{ id: string; nombre: string }[] | null>(null);
  const [duenoId, setDuenoId] = useState('');
  const [pin, setPin] = useState('');
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<DeviceKind>('telefono');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    void api
      .usuarios()
      .then((r) => {
        if (!vivo) return;
        const soloDuenos = r.usuarios.filter((u) => u.rol === 'dueno');
        setDuenos(soloDuenos);
        if (soloDuenos.length === 1) setDuenoId(soloDuenos[0]!.id);
      })
      .catch(() => vivo && setDuenos([]));
    return () => {
      vivo = false;
    };
  }, []);

  const listo = duenoId !== '' && pin.length >= 6 && nombre.trim() !== '' && !ocupado;

  async function registrar() {
    setOcupado(true);
    setError(null);
    try {
      const res = await api.registrarDispositivo({ usuarioId: duenoId, pin, nombre: nombre.trim(), tipo });
      await setMeta('dispositivoId', res.dispositivoId);
      await setMeta('dispositivoCodigo', res.codigo);
      await setMeta('token', res.token);
      // Registering took this device's sequence 1 on the server.
      await setMeta('proximoDeviceSeq', 2);
      await setMeta('ultimoServerSeq', 0);
      await sincronizar();
      onListo();
    } catch (e) {
      setError(
        e instanceof OfflineError
          ? t('instalar.sinInternet')
          : e instanceof ApiError && e.status === 403
            ? t('instalar.pinDuenoIncorrecto')
            : e instanceof ApiError
              ? e.message
              : t('instalar.falloRegistro'),
      );
      setPin('');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Abastece Ya</h1>
          <p className="mt-1 text-sm text-slate-400">{t('instalar.tituloRegistrar')}</p>
        </div>
        <Idioma tamano="compacto" />
      </header>

      <div className="tarjeta flex flex-col gap-4">
        {duenos !== null && duenos.length === 0 ? (
          <p className="text-sm text-amber-300">{t('instalar.sinDuenos')}</p>
        ) : (
          <div>
            <label className="etiqueta" htmlFor="dueno">
              {t('instalar.dueno')}
            </label>
            <select id="dueno" className="campo" value={duenoId} onChange={(e) => setDuenoId(e.target.value)}>
              {duenoId === '' && <option value="">—</option>}
              {(duenos ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nombre}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="etiqueta" htmlFor="pin-dueno">
            {t('instalar.pinDueno')}
          </label>
          <input
            id="pin-dueno"
            className="campo tabular"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            maxLength={10}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
        </div>

        <div>
          <label className="etiqueta" htmlFor="tipo">
            {t('instalar.tipoEquipo')}
          </label>
          <select id="tipo" className="campo" value={tipo} onChange={(e) => setTipo(e.target.value as DeviceKind)}>
            {TIPOS.map((k) => (
              <option key={k} value={k}>
                {t(`personal.tipo.${k}` as Clave)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="etiqueta" htmlFor="nombre-equipo">
            {t('instalar.nombreDispositivo')}
          </label>
          <input
            id="nombre-equipo"
            className="campo"
            value={nombre}
            maxLength={60}
            placeholder={t('instalar.nombreEquipoEjemplo')}
            onChange={(e) => setNombre(e.target.value)}
          />
        </div>
      </div>

      {error && <p className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</p>}

      <button type="button" className="btn-primario w-full" disabled={!listo} onClick={() => void registrar()}>
        <Ico icono="entrar" />
        {t(ocupado ? 'instalar.registrando' : 'instalar.registrar')}
      </button>
    </div>
  );
}
